import type { Graph, Organization, Product, Thing, WebSite, WithContext } from "schema-dts";

export const websiteStructuredData: WithContext<WebSite> = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "ElevenLabs",
  url: "https://elevenlabs.io",
};

export const baseOrgStructuredData = {
  "@type": "Organization",
  name: "ElevenLabs",
  url: "https://elevenlabs.io/",
  logo: {
    "@type": "ImageObject",
    caption: "ElevenLabs logo",
    url: "https://elevenlabs.io/favicon.ico",
  },
  description:
    "ElevenLabs is a voice AI research & deployment company with a mission to make content universally accessible in any language & voice.",
  sameAs: [
    "https://x.com/elevenlabs",
    "https://x.com/ElevenLabsDevs",
    "https://www.linkedin.com/company/elevenlabsio",
    "https://github.com/elevenlabs",
    "https://www.youtube.com/@elevenlabsio",
    "https://discord.gg/elevenlabs",
    "https://www.instagram.com/elevenlabsio/",
    "https://en.wikipedia.org/wiki/ElevenLabs",
    "https://tiktok.com/elevenlabsio",
  ],
  founder: [
    {
      "@type": "Person",
      name: "Mati Staniszewski",
      jobTitle: "Co-Founder",
      sameAs: "https://uk.linkedin.com/in/matiii",
    },
    {
      "@type": "Person",
      name: "Piotr Dabkowski",
      jobTitle: "Co-Founder",
      sameAs: "https://www.linkedin.com/in/piotr-dabkowski-50222bba",
    },
  ],
} as const;

export const orgStructuredData = {
  "@context": "https://schema.org",
  ...baseOrgStructuredData,
} as WithContext<Organization>;

const offersData = [
  {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Free - 10,000 credits per month",
  },
  {
    "@type": "Offer",
    price: "5",
    priceCurrency: "USD",
    description: "Starter Plan - 30,000 credits per month & many other features",
  },
  {
    "@type": "Offer",
    price: "11",
    priceCurrency: "USD",
    description: "Creator Plan - 100,000 credits per month & many other features",
  },
  {
    "@type": "Offer",
    price: "99",
    priceCurrency: "USD",
    description: "Pro Plan - 500,000 credits per month & many other features",
  },
  {
    "@type": "Offer",
    price: "330",
    priceCurrency: "USD",
    description: "Scale Plan - 2,000,000 credits per month & many other features",
  },
  {
    "@type": "Offer",
    price: "1320",
    priceCurrency: "USD",
    description: "Business Plan - 11,000,000 credits per month & many other features",
  },
  {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Enterprise Plan - Volume based discounts & many other features",
  },
] as const;

const defaultRatingData = {
  "@type": "AggregateRating",
  ratingValue: "4.8",
  reviewCount: "7415",
} as const;

const defaultReviewData = [
  {
    "@type": "Review",
    reviewRating: {
      "@type": "Rating",
      ratingValue: "4.8",
      bestRating: "5",
    },
    author: {
      "@type": "Organization",
      name: "Apple iOS App Store",
    },
  },
] as const;

export const ttsStructuredData: WithContext<Product> = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "ElevenLabs Text to Speech",
  offers: {
    "@type": "Offer",
    price: 0,
    priceCurrency: "USD",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    ratingCount: "7415",
  },
};

export const sttStructuredData: WithContext<Product> = {
  "@context": "https://schema.org",
  name: "ElevenLabs Speech to Text",
  description: "High quality, Speech to Text transcription in 99 languages",
  "@type": "Product",
  image: [
    "https://eleven-public-cdn.elevenlabs.io/payloadcms/8rt2oztdckk-speech-to-text-1x1.webp",
    "https://eleven-public-cdn.elevenlabs.io/payloadcms/9ezz1faor19-speech-to-text-4x3.webp",
    "https://eleven-public-cdn.elevenlabs.io/payloadcms/oy2xaupx3c-speech-to-text-16x9.webp",
  ],
  aggregateRating: defaultRatingData,
  review: defaultReviewData,
  offers: offersData,
};

export const sttRealtimeStructuredData: WithContext<Product> = {
  "@context": "https://schema.org",
  name: "ElevenLabs Realtime Speech to Text",
  description: "High quality, realtime Speech to Text transcription in 99 languages",
  "@type": "Product",
  image: [
    "https://eleven-public-cdn.elevenlabs.io/payloadcms/8rt2oztdckk-speech-to-text-1x1.webp",
    "https://eleven-public-cdn.elevenlabs.io/payloadcms/9ezz1faor19-speech-to-text-4x3.webp",
    "https://eleven-public-cdn.elevenlabs.io/payloadcms/oy2xaupx3c-speech-to-text-16x9.webp",
  ],
  aggregateRating: defaultRatingData,
  review: defaultReviewData,
  offers: offersData,
};

export const DefaultStructuredData = () => {
  const structuredData: WithContext<Thing>[] = [orgStructuredData, websiteStructuredData];
  return <StructuredData data={structuredData} id='structured-data' />;
};

export const StructuredData = ({ id, data }: { data: WithContext<Thing>[]; id: string }) => {
  const graph: Graph = {
    "@context": "https://schema.org",
    "@graph": data,
  };
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(graph),
      }}
      id={id}
      type='application/ld+json'
    />
  );
};
